"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

const AUTH_PATHS = [
  "/login",
  "/login-m",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/self-onboarding",
];

const CONTENTSQUARE_TAG_ID =
  process.env.NEXT_PUBLIC_CONTENTSQUARE_ID || "2162f9e9f61ff";

const CONTENTSQUARE_SRC = `https://t.contentsquare.net/uxa/${CONTENTSQUARE_TAG_ID}.js`;

/** Survives React Strict Mode remounts so the tag is only bootstrapped once. */
let hasBootstrappedTag = false;
let lastTrackedPath: string | null = null;

function isAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return AUTH_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));
}

function isEnabled(): boolean {
  return (
    process.env.NODE_ENV === "production" ||
    process.env.NEXT_PUBLIC_HOTJAR_DEBUG === "true"
  );
}

/** Contentsquare path format from the official install snippet. */
function getTrackingPath(): string {
  return window.location.pathname + window.location.hash.replace("#", "?__");
}

function ensureUxa(): void {
  window._uxa = window._uxa || [];
}

/**
 * Official Contentsquare main tag behavior for SPAs:
 * - First load: setPath + inject script
 * - Later navigations: trackPageview
 * Auth routes are excluded so login/register are not recorded.
 */
export default function ContentsquareInit() {
  const pathname = usePathname();
  const onAuthRoute = isAuthPath(pathname);

  useEffect(() => {
    if (!isEnabled() || onAuthRoute) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    ensureUxa();
    const trackingPath = getTrackingPath();

    const tagAlreadyPresent =
      hasBootstrappedTag ||
      typeof window.CS_CONF !== "undefined" ||
      Boolean(document.getElementById("contentsquare"));

    if (!tagAlreadyPresent) {
      window._uxa!.push(["setPath", trackingPath]);

      const script = document.createElement("script");
      script.id = "contentsquare";
      script.type = "text/javascript";
      script.async = true;
      script.src = CONTENTSQUARE_SRC;
      document.head.appendChild(script);

      hasBootstrappedTag = true;
      lastTrackedPath = trackingPath;
      return;
    }

    hasBootstrappedTag = true;

    // Skip duplicate from React Strict Mode remount on the same path
    if (lastTrackedPath === trackingPath) return;

    lastTrackedPath = trackingPath;
    window._uxa!.push(["trackPageview", trackingPath]);
  }, [pathname, onAuthRoute]);

  return null;
}
